import { useMemo, useState, useEffect, useCallback } from "react";
import { Seo } from "@/components/Seo/Seo";
import { Reveal } from "@/components/Reveal/Reveal";
import { getMedia, mediaUrl, type MediaItem } from "@/lib/media";
import { breadcrumbJsonLd } from "@/lib/structuredData";
import { prefersReducedMotion } from "@/lib/motion";
import { CoverflowGallery } from "./CoverflowGallery";
import styles from "./GalleryPage.module.css";

const ALL = "All Photos";

export function GalleryPage() {
  const house = useMemo(() => getMedia("house"), []);
  const wedding = useMemo(() => getMedia("wedding"), []);
  const videos = useMemo(() => getMedia("videos"), []);

  const photos = useMemo<MediaItem[]>(() => {
    const weddingItems = wedding.map((w) => ({ ...w, group: "Weddings & Events" }));
    return [...house, ...weddingItems];
  }, [house, wedding]);

  const groups = useMemo(() => {
    const set = new Set<string>();
    photos.forEach((p) => p.group && set.add(p.group));
    return [ALL, ...Array.from(set)];
  }, [photos]);

  const [filter, setFilter] = useState<string>(ALL);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [immersive, setImmersive] = useState(false);

  // Upgrade to the flying tunnel on the client (unless reduced motion).
  useEffect(() => {
    if (!prefersReducedMotion()) setImmersive(true);
  }, []);

  const visible = useMemo(
    () => (filter === ALL ? photos : photos.filter((p) => p.group === filter)),
    [filter, photos],
  );

  const flyImages = useMemo(
    () => visible.map((p) => ({ src: mediaUrl(p.path), name: p.name })),
    [visible],
  );

  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(
    () => setLightbox((i) => (i === null ? i : (i + 1) % visible.length)),
    [visible.length],
  );
  const prev = useCallback(
    () => setLightbox((i) => (i === null ? i : (i - 1 + visible.length) % visible.length)),
    [visible.length],
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, close, next, prev]);

  const active = lightbox === null ? null : visible[lightbox];

  const seo = (
    <Seo
      title="Property Gallery"
      description="Browse photos and videos of Villa Rosa — the home, grounds, ceremony space, and event areas of our 30-acre Fallbrook estate."
      path="/gallery"
      image={photos[0] ? mediaUrl(photos[0].path) : undefined}
      jsonLd={breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Gallery", path: "/gallery" },
      ])}
    />
  );

  const filterBar = (className: string) => (
    <div className={className} role="tablist" aria-label="Filter photos">
      {groups.map((g) => (
        <button
          key={g}
          type="button"
          role="tab"
          aria-selected={filter === g}
          className={`${styles.filter} ${filter === g ? styles.filterActive : ""}`}
          onClick={() => setFilter(g)}
        >
          {g}
        </button>
      ))}
    </div>
  );

  const lightboxEl = active && (
    <div
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={active.name}
      onClick={close}
    >
      <button className={styles.lbClose} onClick={close} aria-label="Close">
        ×
      </button>
      <button
        className={`${styles.lbNav} ${styles.lbPrev}`}
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        aria-label="Previous"
      >
        ‹
      </button>
      <figure className={styles.lbFigure} onClick={(e) => e.stopPropagation()}>
        <img src={mediaUrl(active.path)} alt={active.name} />
        <figcaption>{active.name}</figcaption>
      </figure>
      <button
        className={`${styles.lbNav} ${styles.lbNext}`}
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        aria-label="Next"
      >
        ›
      </button>
    </div>
  );

  // ---- Immersive scroll-jacked coverflow (client + motion only) ----
  if (immersive) {
    return (
      <>
        {seo}
        <CoverflowGallery
          images={flyImages}
          onSelect={(i) => setLightbox(i)}
          paused={lightbox !== null}
        />
        <div className={styles.fixedFilters}>
          <span className={styles.fixedFiltersLabel}>Gallery</span>
          {filterBar(styles.filters)}
        </div>
        {lightboxEl}
      </>
    );
  }

  // ---- Accessible fallback grid (SSR / reduced motion) ----
  return (
    <>
      {seo}

      <header className={styles.head}>
        <Reveal className="container">
          <p className="eyebrow eyebrow--rule">Property Gallery</p>
          <h1>Take a look around Villa Rosa</h1>
          <p className={styles.lede}>
            From the panoramic ceremony space to the cozy interiors, explore
            every corner of our 30-acre estate.
          </p>
        </Reveal>
      </header>

      <section className="section">
        <div className="container">
          {filterBar(styles.filters)}

          <div className={styles.grid}>
            {visible.map((item, i) => (
              <Reveal
                as="button"
                key={item.path}
                variant="scale"
                delay={(i % 6) * 55}
                className={styles.tile}
                type="button"
                onClick={() => setLightbox(i)}
                aria-label={`View ${item.name}`}
              >
                <img
                  src={mediaUrl(item.path)}
                  alt={item.name}
                  loading="lazy"
                  decoding="async"
                />
                <span className={styles.tileCaption}>{item.name}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {videos.length > 0 && (
        <section className="section section--alt">
          <div className="container">
            <div className={styles.head2}>
              <p className="eyebrow eyebrow--rule">Video Tour</p>
              <h2>See Villa Rosa in motion</h2>
            </div>
            <div className={styles.videoGrid}>
              {videos.slice(0, 9).map((v) => (
                <video
                  key={v.path}
                  className={styles.video}
                  controls
                  preload="none"
                  playsInline
                  poster={undefined}
                >
                  <source src={mediaUrl(v.path)} type="video/mp4" />
                </video>
              ))}
            </div>
          </div>
        </section>
      )}

      {lightboxEl}
    </>
  );
}
